/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable no-param-reassign */
import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from '../../hooks';
import { Dispatchers } from '../dispatchers.interface';
import { Alert, Selectors } from '../selectors.interface';
import { Team } from './team.interfaces';
import { createTeamThunk } from './teamCreate.thunk';
import { deleteTeamThunk } from './teamDelete.thunk';
import { loadTeamsThunk } from './teamLoad.thunk';
import { updateTeamThunk } from './teamUpdate.thunk';

// https://redux-toolkit.js.org/api/createEntityAdapter#return-value
const entityAdapter = createEntityAdapter<Team>();

export const teamsSlice = createSlice({
  name: 'teams',
  initialState: entityAdapter.getInitialState<{
    loading: string;
    errors: Alert[];
  }>({
    // ids: [],
    // entities: {},
    loading: 'idle',
    errors: [],
  }),
  reducers: {
    loading(state) {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    },
    receivedAll(state, action) {
      if (state.loading === 'pending') {
        // Or, call them as "mutating" helpers in a case reducer
        entityAdapter.setAll(state, action.payload);
        state.loading = 'idle';
      }
    },
    // Can pass adapter functions directly as case reducers.  Because we're passing this as a value, `createSlice` will auto-generate the `playerAdded` action type / creator
    addedOne: entityAdapter.addOne,
    updatedOne: entityAdapter.updateOne,
    upsertOne: entityAdapter.upsertOne,
    deletedOne: entityAdapter.removeOne,
    addErrors: (state, { payload }: PayloadAction<Alert[]>) => {
      state.errors.push(...payload);
    },
    clearError: (state, { payload }: PayloadAction<string>) => {
      state.errors = state.errors.filter((error) => error.id !== payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadTeamsThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    });
    builder.addCase(loadTeamsThunk.fulfilled, (state, action) => {
      state.loading = 'idle';
      entityAdapter.setAll(state, action.payload);
      // TODO: remove only the error of this request
      state.errors = [];
    });
    builder.addCase(
      loadTeamsThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.errors.push(action.payload);
        state.loading = 'idle';
      },
    );
    builder.addCase(createTeamThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    });
    builder.addCase(createTeamThunk.fulfilled, (state, action) => {
      state.loading = 'idle';
      // TODO: remove only the error of this request
      state.errors = [];
    });
    builder.addCase(
      createTeamThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.errors.push(action.payload);
        state.loading = 'idle';
      },
    );
    builder.addCase(updateTeamThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    });
    builder.addCase(updateTeamThunk.fulfilled, (state, action) => {
      state.loading = 'idle';
      // TODO: remove only the error of this request
      state.errors = [];
    });
    builder.addCase(
      updateTeamThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.errors.push(action.payload);
        state.loading = 'idle';
      },
    );
    builder.addCase(deleteTeamThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    });
    builder.addCase(deleteTeamThunk.fulfilled, (state, action) => {
      state.loading = 'idle';
      // TODO: remove only the error of this request
      state.errors = [];
    });
    builder.addCase(
      deleteTeamThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.errors.push(action.payload);
        state.loading = 'idle';
      },
    );
  },
});

// Can create a set of memoized selectors based on the location of this entity state. simpleSelectors or globalizedSelectors.
// https://redux-toolkit.js.org/api/createEntityAdapter#selector-functions
export const teamSelectors = entityAdapter.getSelectors<RootState>(
  (state) => state.teamsReducer,
);

export const errorsSelector = (state: RootState): Alert[] =>
  state.teamsReducer.errors;

export const isLoadingSelector = (state: RootState): boolean =>
  state.teamsReducer.loading !== 'idle';

export const defaultSelectors: Selectors<Team> = {
  resourcesSelector: teamSelectors.selectAll,
  errorsSelector,
  isLoadingSelector,
};

export const { actions } = teamsSlice;

export const defaultDispatchers: Dispatchers<Team> = {
  loading: actions.loading,
  addResources: actions.receivedAll,
  addResource: actions.addedOne,
  updateResource: actions.updatedOne,
  upsertOne: actions.upsertOne,
  deleteResource: actions.deletedOne,
  addErrors: actions.addErrors,
  clearError: actions.clearError,
};
